import {
    FunctionsObject,
    ReturnTypes,
    AuthenticationError,
} from '@superbet-group/web.lib.utilities';
import {
    xorBy as _xorBy,
    xor as _xor,
    get as _get,
    set as _set,
} from 'lodash';
import Vue from 'vue';
import {
    ActionContext,
    Action,
    Commit,
    Store,
    MutationPayload,
    ActionPayload,
} from 'vuex';

import { logger } from '@app/logger';

import { IState } from '.';
import { RelativeRootGetters } from './types';

export default class StoreUtil {
    /**
     * @deprecated The method should not be used going forward, use `asyncErrorGuard` from `@superbet-group/web.lib.vue-utilities` instead
     */
    static asyncErrorGuard<S = any, R = any>(f: AnyFunction): Action<S, R> {
        return async (
            context: ActionContext<any, any>,
            ...args: any[]
        ) => {
            try {
                return await f(context, ...args);
            } catch (e) {
                if (!(e instanceof AuthenticationError)) {
                    logger.error(e as Error);
                }
                throw e;
            }
        };
    }

    static createSimpleMutatorAction = (mutation: string) =>
        ({ commit }: { commit: Commit }, ...args: any[]) => commit(mutation, ...args);

    /// //// Lists START
    /**
     * @deprecated The method should not be used going forward, use `createSimpleMutator` from `@superbet-group/web.lib.vue-utilities` instead
     */
    static createSimpleMutator = <T = any>(property: Extract<keyof T, string>) =>
        (state: any, value: any) =>
            _set(state, property, value);

    static createPushToListMutator = (property: string) => (state: any, value: any) => state[property].push(value);

    static createPushOrInsertToListMutator = (property: string, valueKey: string) => (
        state: any,
        payload: any,
    ) => {
        const index = payload.index;

        if (index !== null && index !== undefined) {
            state[property].splice(index, 0, payload[valueKey]);
        } else {
            state[property].push(payload[valueKey]);
        }
    };

    static createRemoveFromListMutator = (property: string) => (state: any, value: any) => {
        const index = state[property].indexOf(value);

        if (index === -1) {
            return;
        }

        state[property].splice(index, 1);
    };

    static createToggleListElementMutator = (property: string) =>
        (state: any, value: any) => state[property] = _xor(state[property], [value]);

    static createClearListMutator = (property: string) =>
        (state: any) => state[property] = [];
    /// //// Lists END

    /**
     * @deprecated The method should not be used going forward, use `createSimpleGetter` from `@superbet-group/web.lib.vue-utilities` instead
     */
    static createSimpleGetter = <T = any>(property: Extract<keyof T, string>, defaultValue?: any) =>
        (state: any) => _get(state, property, defaultValue);

    static createToggleListElementMutatorBy = (property: string, key: string) =>
        (state: any, value: any) => state[property] = _xorBy(state[property], [value], key);

    static createToggleMutator = (property: string) =>
        (state: any, value?: any) => {
            if (value === undefined || value === _get(state, property)) {
                _set(state, property, !_get(state, property));
            } else {
                _set(state, property, value);
            }
        };

    /**
     * @deprecated The method should not be used going forward, use `createNestedToggleMutator` from `@superbet-group/web.lib.vue-utilities` instead
     */
    static createSetMutator = (setPropertyName: string) =>
        (state: any, { key, value }: { key: string | number; value?: boolean }) => {
            if (typeof value === 'boolean') {
                Vue.set(state[setPropertyName], `${key}`, value);
            } else {
                Vue.set(state[setPropertyName], `${key}`, !state[setPropertyName][key]);
            }
        };

    static createActionSubscriptionPromise(store: Store<any>, actionType: string) {
        return new Promise<void>((resolve) => {
            const unsubscribe = store.subscribeAction((action: ActionPayload) => {
                if (action.type === actionType) {
                    unsubscribe();
                    resolve();
                }
            });
        });
    }

    static createMutationSubscriptionPromise(store: Store<any>, actionType: string) {
        return new Promise<void>((resolve) => {
            const unsubscribe = store.subscribe((action: MutationPayload) => {
                if (action.type === actionType) {
                    unsubscribe();
                    resolve();
                }
            });
        });
    }

    static createClearCopiedTicketWatcher(store: Store<unknown>) {
        return store.watch(
            (_state, getters) => getters['ui/sportOffer/betSlip/areAllInstancesEmpty'],
            (areEmpty: boolean) => {
                if (areEmpty) {
                    store.dispatch('data/tickets/setCopiedTicket', null, { root: true });
                }
            },
        );
    }
}

// a getter definition used when defining getters in a module

export type Getter<S, G, T> = (
    state: S,
    getters: G,
    rootState: IState,
    rootGetters: RelativeRootGetters,
) => T;

/* a type that returns typings for getters which other getter and actions
    receive */
export type ComputedGetters<T extends FunctionsObject> = ReturnTypes<T>;
